import React, { useEffect, useRef, useState } from "react";

// Info dot on the page title (design 1a desktop / 1f Phone 4 mobile).
//
// Trigger: a small bordered "i" circle (S11). Open state renders
//   - desktop (sm+): an arrowed popover to the right of the trigger (S27),
//   - mobile: a bottom sheet with grab handle (S22), title and a "Got it"
//     button; the trigger fills solid blue while the sheet is open.
// Pure presentation — no wiring; closes on outside click, Escape, scrim tap
// or the "Got it" button.
const InfoDot = ({ text, sheetTitle, confirmLabel }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        aria-label={sheetTitle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={[
          "w-[19px] h-[19px] sm:w-5 sm:h-5 rounded-full border-[1.5px] box-border",
          "inline-flex items-center justify-center text-[11.5px] font-extrabold leading-none cursor-pointer",
          // Mobile active state fills solid blue (1f Phone 4); desktop keeps
          // the light style with the popover open (1a).
          open
            ? "border-[#2283FF] bg-[#2283FF] text-white sm:border-[#9CC6FF] sm:bg-[#F4F9FF] sm:text-[#2283FF] sm:hover:bg-[#EAF3FF]"
            : "border-[#9CC6FF] bg-[#F4F9FF] text-[#2283FF] hover:bg-[#EAF3FF]",
        ].join(" ")}
      >
        i
      </button>

      {open && (
        <>
          {/* Desktop popover (1a) — 308px, arrowed, right of the trigger */}
          <span
            role="note"
            className="hidden sm:block absolute -top-2 left-[34px] w-[308px] bg-white border border-[#E7EBF1] rounded-xl shadow-[0_16px_40px_rgba(16,27,44,0.14)] px-[15px] py-[13px] z-[6]"
          >
            <span className="absolute top-3.5 -left-[5.5px] w-2.5 h-2.5 bg-white border-l border-b border-[#E7EBF1] rotate-45 block" />
            <span className="block text-[12.5px] leading-[1.55] text-[#4A5B72] font-medium">
              {text}
            </span>
          </span>

          {/* Mobile bottom sheet (1f Phone 4) */}
          <span className="sm:hidden fixed inset-0 z-50 block" role="dialog" aria-label={sheetTitle}>
            <span
              className="absolute inset-0 bg-[rgba(16,27,44,0.4)] block"
              onClick={() => setOpen(false)}
            />
            <span className="absolute left-0 right-0 bottom-0 bg-white rounded-t-[22px] shadow-[0_-16px_44px_rgba(16,27,44,0.2)] px-5 pt-3 pb-[42px] block">
              <span className="block w-9 h-1 rounded-full bg-[#D7DEE7] mx-auto" />
              <span className="block mt-[18px] text-[17px] font-extrabold text-[#101B2C] tracking-[-0.01em]">
                {sheetTitle}
              </span>
              <span className="block mt-2 text-[14.5px] leading-[1.6] text-[#4A5B72]">
                {text}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mt-[18px] flex items-center justify-center w-full h-[50px] rounded-[14px] bg-[#2283FF] hover:bg-[#1668D9] text-white text-[15px] font-bold"
              >
                {confirmLabel}
              </button>
            </span>
          </span>
        </>
      )}
    </span>
  );
};

export default InfoDot;
